import { Alert, AlertDescription, Button } from "@devhop/ui";
import { AlertTriangleIcon, MapPinIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { useFindAlternativeRooms } from "@/modules/classes/presentation/api/queries";
import { formatTime } from "@/shared/lib/date-time";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import type { RehearsalDateConflictDetail } from "./rehearsal-studio-conflict";

type RehearsalStudioConflictAlertProps = {
  message: string;
  details: RehearsalDateConflictDetail[];
  studioRoomId?: string;
  onRemoveDate: (date: string) => void;
  onSelectRoom: (roomId: string) => void;
  disabled?: boolean;
  showRemoveDate?: boolean;
};

function formatConflictItemLabel(session: {
  templateName: string;
  startTime: string;
  endTime: string;
  instructorNames: string[];
}) {
  const timeRange = `${formatTime(session.startTime)}–${formatTime(session.endTime)}`;
  const instructors =
    session.instructorNames.length > 0
      ? ` (${session.instructorNames.join(", ")})`
      : "";
  return `${session.templateName} — ${timeRange}${instructors}`;
}

export function RehearsalStudioConflictAlert({
  message,
  details,
  studioRoomId,
  onRemoveDate,
  onSelectRoom,
  disabled,
  showRemoveDate = true,
}: RehearsalStudioConflictAlertProps) {
  const findAlternatives = useFindAlternativeRooms();
  const [showAlternatives, setShowAlternatives] = useState(false);

  const firstDetail = details[0];
  const canSearchAlternatives =
    !!firstDetail?.date &&
    !!firstDetail.slotStartTime &&
    !!firstDetail.slotEndTime;

  const handleFindAlternatives = async () => {
    if (!canSearchAlternatives || !firstDetail) return;
    setShowAlternatives(true);
    await findAlternatives.mutateAsync({
      date: firstDetail.date,
      startTime: firstDetail.slotStartTime,
      endTime: firstDetail.slotEndTime,
      excludeRoomId: studioRoomId,
    });
  };

  return (
    <Alert variant="destructive" className="sm:col-span-2">
      <AlertDescription>
        <div className="flex items-start gap-2">
          <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1 space-y-3">
            <p className="font-medium">{message}</p>

            {details.length > 0 ? (
              <div className="overflow-x-auto rounded-md border border-destructive/25 bg-background/80">
                <table className="w-full min-w-[32rem] border-collapse text-sm">
                  <thead>
                    <tr className="border-destructive/20 border-b text-left text-muted-foreground">
                      <th className="px-3 py-2 font-medium">ວັນທີ</th>
                      <th className="px-3 py-2 font-medium">ເວລາທີ່ຈອງ</th>
                      <th className="px-3 py-2 font-medium">ຂັດແຍ້ງກັບ</th>
                      {showRemoveDate ? (
                        <th className="px-3 py-2 font-medium">ຈັດการ</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((item) => (
                      <tr
                        key={item.date}
                        className="border-destructive/10 border-b last:border-0"
                      >
                        <td className="px-3 py-2 align-top font-medium">
                          {formatDateLocal(item.date)}
                        </td>
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          {formatTime(item.slotStartTime)}–
                          {formatTime(item.slotEndTime)}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {item.result.conflictingSessions.length > 0 ? (
                            <ul className="space-y-1">
                              {item.result.conflictingSessions.map((session) => (
                                <li
                                  key={session.id}
                                  className="rounded-md border border-destructive/15 bg-destructive/5 px-2 py-1 text-xs"
                                >
                                  {formatConflictItemLabel(session)}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              ຫ້ອງບໍ່ວ່າງໃນເວລານີ້
                            </span>
                          )}
                        </td>
                        {showRemoveDate ? (
                          <td className="px-3 py-2 align-top">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 border-destructive/30 text-destructive hover:bg-destructive/10"
                              disabled={disabled}
                              onClick={() => onRemoveDate(item.date)}
                            >
                              <Trash2Icon className="size-3.5" />
                              ລຶບວັນນີ້
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {canSearchAlternatives ? (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-destructive/30"
                  disabled={disabled || findAlternatives.isPending}
                  onClick={() => void handleFindAlternatives()}
                >
                  {findAlternatives.isPending
                    ? "ກຳລັງຄົ້ນຫາ..."
                    : "ແນະນຳຫ້ອງທາງເລືອກ"}
                </Button>

                {showAlternatives &&
                findAlternatives.data?.rooms &&
                findAlternatives.data.rooms.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="font-medium text-xs">ຫ້ອງທີ່ມີຢູ່:</p>
                    {findAlternatives.data.rooms.map((room) => (
                      <Button
                        key={room.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-full justify-start"
                        disabled={disabled}
                        onClick={() => onSelectRoom(room.id)}
                      >
                        <MapPinIcon className="mr-2 size-3.5" />
                        {room.name}
                      </Button>
                    ))}
                  </div>
                ) : null}

                {showAlternatives &&
                findAlternatives.data?.rooms &&
                findAlternatives.data.rooms.length === 0 ? (
                  <p className="text-muted-foreground text-xs">
                    ບໍ່ມີຫ້ອງທາງເລືອກທີ່ມີຢູ່ສຳລັບເວລານີ້
                  </p>
                ) : null}
              </div>
            ) : null}

            <p className="text-muted-foreground text-xs">
              {showRemoveDate
                ? "ລຶບວັນທີ່ຂັດແຍ້ງ ຫຼື ເລືອກຫ້ອງອື່ນ / ປ່ຽນເວລາ — ຈຶ່ງບັນທຶກໄດ້"
                : "ປ່ຽນວັນໃໝ່ ເລືອກຫ້ອງອື່ນ ຫຼື ປັບເວລາ — ຈຶ່ງບັນທຶກໄດ້"}
            </p>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
